import { TabViewAccess } from '@brighthub/admin-contract';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ChatComponent,
  ConnectorStatusComponent,
  CopilotFacadeService,
} from '@brighthub/copilot';
import { ClientListComponent } from '@brightnav/ui';
import { ActivityInboxService, defaultFilterCount } from '@brightnav/review';
import { FeatureFlagKey, FeatureFlagsService } from '@brighthub/feature-flags';
import { Splitter } from 'primeng/splitter';
import { map } from 'rxjs';
import { BrightnavLayoutComponent } from '../brightnav-layout/brightnav-layout.component';
import { BrightnavLayoutService } from '../brightnav-layout.service';
import { ClientSessionService } from '@brighthub/uisession';
import { TabItem } from '../tab-bar/tab-bar.component';
import { FaClientInfoPanelComponent } from '../fa-client-info-panel/fa-client-info-panel.component';
import type { FaAppTabId } from '../fa-tabs';
import { CARE_PLAN_ACCESS } from '@brightnav/care-plan';

export type { FaAppTabId } from '../fa-tabs';

function isFlagOn(value: unknown): boolean {
  return value !== false && value !== null && value !== undefined;
}

@Component({
  selector: 'bn-fa-app-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ChatComponent,
    ConnectorStatusComponent,
    ClientListComponent,
    BrightnavLayoutComponent,
    FaClientInfoPanelComponent,
    Splitter,
  ],
  templateUrl: './fa-app-layout.component.html',
  styleUrl: './fa-app-layout.component.scss',
})
export class FaAppLayoutComponent {
  layoutService = inject(BrightnavLayoutService);
  clientSessionService = inject(ClientSessionService);
  private _copilotFacadeService = inject(CopilotFacadeService);
  private readonly _featureFlags = inject(FeatureFlagsService);
  private readonly _inbox = inject(ActivityInboxService);
  private readonly _carePlanAccess = inject(CARE_PLAN_ACCESS);
  private readonly _tabViewAccess = inject(TabViewAccess);
  collapsed = computed(() => this.layoutService.collapsed());

  /** Display label for the chat panel header. */
  readonly copilotPanelLabel = signal('Navi');

  /** Agent whose connector requirements belong in the expanded chat header. */
  readonly selectedAgentIdentifier = toSignal(
    this._copilotFacadeService.selectedAgentIdentifier$,
    { initialValue: null },
  );

  /** Active tab in the tab-style menubar; drives which content shows left of chat. */
  readonly activeTab = signal<FaAppTabId>('care-plan');

  /** When true, chat panel is collapsed to a narrow strip with expand button. */
  readonly copilotCollapsed = signal(false);

  /** When true, main content panel (tab bar + content) is collapsed to a narrow left strip. */
  readonly contentPanelCollapsed = signal(false);

  /** Finances tab visibility — whole tab hidden when flag off. */
  readonly financialSnapshotEnabled = toSignal(
    this._featureFlags
      .getValueChanges$(FeatureFlagKey.ENABLEFINANCIALSNAPSHOT, false)
      .pipe(map((v) => isFlagOn(v))),
    { initialValue: false },
  );

  /**
   * Care Plan defaults active, so distinguish a flag that is still resolving
   * from one that has resolved off. This avoids briefly selecting Profile
   * while LaunchDarkly initializes.
   */
  private readonly _carePlanFlagState = toSignal(
    this._featureFlags
      .getValueChanges$(FeatureFlagKey.ENABLECAREPLAN, false)
      .pipe(map((v) => ({ enabled: isFlagOn(v), resolved: true }))),
    { initialValue: { enabled: false, resolved: false } },
  );

  /** Care Plan tab visibility — whole tab hidden when flag off (MW-11141, non-deployable foundation). */
  readonly carePlanEnabled = computed(() => this._carePlanFlagState().enabled);

  /** Finances tab may also be introduced by the independently flagged Credit section. */
  readonly creditOverviewEnabled = toSignal(
    this._featureFlags
      .getValueChanges$(FeatureFlagKey.ENABLECREDITOVERVIEW, false)
      .pipe(map((v) => isFlagOn(v))),
    { initialValue: false },
  );

  readonly financesEnabled = computed(
    () => this.financialSnapshotEnabled() || this.creditOverviewEnabled(),
  );

  /** Accounts tab visibility — whole tab hidden when flag off. */
  readonly accountsTabEnabled = toSignal(
    this._featureFlags
      .getValueChanges$(FeatureFlagKey.ENABLEACCOUNTSTAB, false)
      .pipe(map((v) => isFlagOn(v))),
    { initialValue: false },
  );

  /** Review tab visibility — whole tab hidden when flag off. */
  readonly aiReviewEnabled = toSignal(
    this._featureFlags
      .getValueChanges$(FeatureFlagKey.ENABLEAIREVIEW, false)
      .pipe(map((v) => isFlagOn(v))),
    { initialValue: false },
  );

  toggleCopilot(): void {
    this.copilotCollapsed.update((v) => !v);
  }

  toggleContentPanel(): void {
    this.contentPanelCollapsed.update((v) => !v);
  }

  /** Active tab item for the collapsed content strip (label + icon). */
  readonly activeTabItem = computed(() => {
    const id = this.activeTab();
    return this.tabItems().find((t) => t.id === id) ?? null;
  });

  onTabSelect(tabId: string): void {
    if (this.tabItems().some((tab) => tab.id === tabId)) {
      this.activeTab.set(tabId as FaAppTabId);
    }
  }

  /**
   * Tab IDs that are always pinned in FA layout (cannot be unpinned).
   * Flag-gated IDs can remain here because the tab bar only renders IDs that
   * are present in `tabItems`.
   */
  readonly faMandatoryTabIds = signal<FaAppTabId[]>([
    'care-plan',
    'profile',
    'finances',
    'accounts',
  ]);

  /** GUID of the client whose page is open, or null before one is fetched.
   *
   * Tracks `selectedSubjectData`, which the session service sets whether the
   * user typed a fresh GUID or picked one of their already-open clients, and
   * clears when the selected client is archived. */
  readonly selectedClientGuid = computed(
    () => this.clientSessionService.selectedSubjectData()?.guid ?? null,
  );

  /** "Taniesha Duncan" rather than a GUID, for the Review tab's header and
   * detail dialog. Null until a client is selected. */
  readonly selectedClientName = computed(() => {
    const subject = this.clientSessionService.selectedSubjectData();
    if (!subject) return null;
    return [subject.firstName, subject.lastName].filter(Boolean).join(' ');
  });

  /** Default-filter count for the Review tab badge (`needs_confirmation` +
   * `error`). Hidden at 0. The queue re-emits after confirm/deny so this
   * cannot go stale while the tab is open. */
  readonly reviewDefaultCount = signal(0);

  /** Discards badge responses superseded by a newer client selection. */
  private _reviewBadgeLoadToken = 0;

  /**
   * Tab items for bn-tab-bar (id, label, icon).
   * Care Plan is first when its flag is on; Finances and Accounts follow
   * Profile when their flags are on.
   * Review only when the flag is on and a client is open — there is nothing for it to scope to otherwise.
   */
  readonly tabItems = computed<TabItem[]>(() => {
    const items: TabItem[] = [];
    if (this.carePlanEnabled() && this._carePlanAccess.canView()) {
      items.push({
        id: 'care-plan',
        label: 'Care Plan',
        icon: 'pi pi-clipboard',
      });
    }
    if (this._tabViewAccess.canView('profile')) {
      items.push({ id: 'profile', label: 'Profile', icon: 'pi pi-user' });
    }
    if (this.financesEnabled() && this._tabViewAccess.canView('finances')) {
      items.push({ id: 'finances', label: 'Finances', icon: 'pi pi-wallet' });
    }
    if (this.accountsTabEnabled()) {
      items.push({
        id: 'accounts',
        label: 'Accounts',
        icon: 'pi pi-credit-card',
      });
    }
    if (this.aiReviewEnabled() && this.selectedClientGuid()) {
      items.push({
        id: 'review',
        label: 'Review',
        icon: 'pi pi-inbox',
        badgeCount: this.reviewDefaultCount(),
      });
    }
    return items;
  });

  constructor() {
    effect(() => {
      if (this._copilotFacadeService.panelOpenRequested() > 0) {
        this.copilotCollapsed.set(false);
      }
    });
    effect(() => {
      const selectedSession = this.clientSessionService.selectedSession();
      if (!selectedSession) {
        this._copilotFacadeService.clearContext();
      }
    });

    // Feature flags and client selection can remove tabs from the current
    // item list. Never leave the content pane pointed at a missing tab.
    effect(() => {
      const activeTab = this.activeTab();
      if (
        activeTab === 'care-plan' &&
        !this._carePlanFlagState().resolved &&
        this._carePlanAccess.canView()
      ) {
        return;
      }
      if (!this.tabItems().some((tab) => tab.id === activeTab)) {
        const tabs = this.tabItems();
        const fallback = tabs.find((tab) => tab.id === 'profile') ?? tabs[0];
        // With no available tabs, the panel's availability guard renders no content.
        this.activeTab.set((fallback?.id ?? 'profile') as FaAppTabId);
      }
    });

    // Read the client's activities on selection rather than on tab open, so
    // the badge is already right when the tab bar first renders. Skip when the
    // flag is off so we do not hit the inbox API for a hidden tab. A failure
    // just leaves the badge off — the tab itself surfaces the error when
    // opened.
    effect(() => {
      const guid = this.selectedClientGuid();
      this.reviewDefaultCount.set(0);
      if (!this.aiReviewEnabled() || !guid) return;
      const token = ++this._reviewBadgeLoadToken;
      const requestedGuid = guid;
      void this._inbox
        .loadClientFeed(requestedGuid)
        .then((feed) => {
          if (token !== this._reviewBadgeLoadToken) return;
          if (this.selectedClientGuid() !== requestedGuid) return;
          this.reviewDefaultCount.set(defaultFilterCount(feed.countsByStatus));
        })
        .catch(() => {
          if (token !== this._reviewBadgeLoadToken) return;
          if (this.selectedClientGuid() !== requestedGuid) return;
          this.reviewDefaultCount.set(0);
        });
    });
  }

  ngOnInit() {
    this._copilotFacadeService.hideInput.set(false);
  }
}
